import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

export const DarkModeProvider = ({ children }) => {
  // Get initial mode from localStorage or default to light
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'dark' ? 'dark' : 'light';
  });

  // Save to localStorage whenever mode changes
  useEffect(() => {
    localStorage.setItem('darkMode', mode);
  }, [mode]);

  const toggleDarkMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Create theme based on mode
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                // Light mode palette
                primary: {
                  main: '#2563eb',
                  light: '#3b82f6',
                  dark: '#1d4ed8',
                  contrastText: '#ffffff',
                },
                secondary: {
                  main: '#64748b',
                  light: '#94a3b8',
                  dark: '#475569',
                  contrastText: '#ffffff',
                },
                background: {
                  default: '#f8fafc',
                  paper: '#ffffff',
                },
                text: {
                  primary: '#1e293b',
                  secondary: '#64748b',
                },
                divider: '#e2e8f0',
                grey: {
                  25: '#fafafa',
                  50: '#f8fafc',
                  100: '#f1f5f9',
                  200: '#e2e8f0',
                  300: '#cbd5e1',
                  400: '#94a3b8',
                  500: '#64748b',
                  600: '#475569',
                  700: '#334155',
                  800: '#1e293b',
                  900: '#0f172a',
                },
                success: {
                  main: '#10b981',
                  light: '#34d399',
                  dark: '#059669',
                  50: '#d1fae5',
                },
                warning: {
                  main: '#f59e0b',
                  light: '#fbbf24',
                  dark: '#d97706',
                  50: '#fef3c7',
                },
                error: {
                  main: '#ef4444',
                  light: '#f87171',
                  dark: '#dc2626',
                  50: '#fee2e2',
                },
                info: {
                  main: '#3b82f6',
                  light: '#60a5fa',
                  dark: '#2563eb',
                  50: '#dbeafe',
                },
              }
            : {
                // Dark mode palette
                primary: {
                  main: '#3b82f6',
                  light: '#60a5fa',
                  dark: '#2563eb',
                  contrastText: '#ffffff',
                },
                secondary: {
                  main: '#94a3b8',
                  light: '#cbd5e1',
                  dark: '#64748b',
                  contrastText: '#ffffff',
                },
                background: {
                  default: '#0f172a',
                  paper: '#1e293b',
                },
                text: {
                  primary: '#f1f5f9',
                  secondary: '#94a3b8',
                },
                divider: '#334155',
                grey: {
                  25: '#1a2332',
                  50: '#1e293b',
                  100: '#334155',
                  200: '#475569',
                  300: '#64748b',
                  400: '#94a3b8',
                  500: '#cbd5e1',
                  600: '#e2e8f0',
                  700: '#f1f5f9',
                  800: '#f8fafc',
                  900: '#ffffff',
                },
                success: {
                  main: '#10b981',
                  light: '#34d399',
                  dark: '#059669',
                  50: '#064e3b',
                },
                warning: {
                  main: '#f59e0b',
                  light: '#fbbf24',
                  dark: '#d97706',
                  50: '#78350f',
                },
                error: {
                  main: '#ef4444',
                  light: '#f87171',
                  dark: '#dc2626',
                  50: '#7f1d1d',
                },
                info: {
                  main: '#3b82f6',
                  light: '#60a5fa',
                  dark: '#2563eb',
                  50: '#1e3a8a',
                },
              }),
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
          },
          h2: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.3,
          },
          h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.3,
          },
          h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
          },
          h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
          },
          h6: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.4,
          },
          body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
          },
          body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
          },
        },
        shape: {
          borderRadius: 12,
        },
        shadows:
          mode === 'light'
            ? [
                'none',
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                ...Array(19).fill('0 25px 50px -12px rgba(0, 0, 0, 0.25)'),
              ]
            : [
                'none',
                '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
                '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
                '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
                '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                ...Array(19).fill('0 25px 50px -12px rgba(0, 0, 0, 0.5)'),
              ],
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarColor: mode === 'dark' ? '#475569 #1e293b' : '#cbd5e1 #f1f5f9',
                '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                  width: 10,
                  height: 10,
                },
                '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                  borderRadius: 8,
                  backgroundColor: mode === 'dark' ? '#475569' : '#cbd5e1',
                  minHeight: 24,
                },
                '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: mode === 'dark' ? '#64748b' : '#94a3b8',
                },
                '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
                  backgroundColor: mode === 'dark' ? '#1e293b' : '#f1f5f9',
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 6,
                fontSize: '0.8125rem',
                minHeight: '28px',
                transition: 'all 0.15s ease',
              },
              sizeSmall: {
                padding: '4px 12px',
                fontSize: '0.8125rem',
                minHeight: '28px',
              },
              sizeMedium: {
                padding: '6px 16px',
                fontSize: '0.875rem',
                minHeight: '32px',
              },
              sizeLarge: {
                padding: '8px 20px',
                fontSize: '0.9375rem',
                minHeight: '36px',
              },
              contained: {
                boxShadow:
                  mode === 'light'
                    ? '0 1px 2px 0 rgba(0, 0, 0, 0.1)'
                    : '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  boxShadow:
                    mode === 'light'
                      ? '0 2px 4px 0 rgba(0, 0, 0, 0.15)'
                      : '0 2px 4px 0 rgba(0, 0, 0, 0.4)',
                },
              },
              outlined: {
                borderWidth: '1px',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow:
                  mode === 'light'
                    ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
                borderRadius: 12,
                backgroundImage: 'none',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
              elevation1: {
                boxShadow:
                  mode === 'light'
                    ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
              },
              elevation2: {
                boxShadow:
                  mode === 'light'
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    : '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
              },
              elevation3: {
                boxShadow:
                  mode === 'light'
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow:
                  mode === 'light'
                    ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
                backgroundImage: 'none',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundImage: 'none',
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#f8fafc' : '#1e293b',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
              },
              head: {
                backgroundColor: mode === 'light' ? '#f8fafc' : '#1e293b',
                fontWeight: 600,
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#f8fafc' : '#334155 !important',
                },
              },
            },
          },
          MuiTablePagination: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                '&:before': {
                  display: 'none',
                },
              },
            },
          },
          MuiAccordionSummary: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#f8fafc' : '#334155',
                },
              },
            },
          },
          MuiAccordionDetails: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#fafafa' : '#0f172a',
              },
            },
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#f8fafc' : '#334155 !important',
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  const value = {
    mode,
    toggleDarkMode,
    isDark: mode === 'dark',
  };

  return (
    <DarkModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </DarkModeContext.Provider>
  );
};
